// ─────────────────────────────────────────────────────────────────────────────
// useBodyScrollLock.ts — Prevents the page behind a mobile sheet from scrolling
//
// Plain `overflow: hidden` on <body> is known to be ignored by iOS Safari for
// touch-scroll bleed-through. Locking via `position: fixed` instead is the
// robust technique: it pins the body at its current scroll offset, then
// restores the exact position on unlock.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef } from 'react';

export function useBodyScrollLock(isLocked: boolean) {
  const scrollY = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    scrollY.current = window.scrollY;
    const { body } = document;
    const previous = {
      position: body.style.position,
      top:      body.style.top,
      left:     body.style.left,
      right:    body.style.right,
      width:    body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top      = `-${scrollY.current}px`;
    body.style.left     = '0';
    body.style.right    = '0';
    body.style.width    = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = previous.position;
      body.style.top      = previous.top;
      body.style.left     = previous.left;
      body.style.right    = previous.right;
      body.style.width    = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY.current);
    };
  }, [isLocked]);
}
